import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { messageService } from '../services/api';
import MessagesSidebar from '../components/MessagesSidebar';

const MessageDetail = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { language } = useLanguage();
    const isRtl = language === 'fa';
    const t = (fa, en) => (isRtl ? fa : en);

    const [message, setMessage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [replyText, setReplyText] = useState('');
    const [replyAttachment, setReplyAttachment] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [replyError, setReplyError] = useState('');

    useEffect(() => {
        const fetchMessage = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await messageService.getMessageReplies(id);
                setMessage(data);
            } catch (err) {
                console.error('Message detail API error:', err);
                setError(t('خطا در دریافت پیام', 'Error fetching message'));
            } finally {
                setLoading(false);
            }
        };
        fetchMessage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleReplyFileChange = (e) => {
        const selected = e.target.files?.[0];
        if (selected) setReplyAttachment(selected);
    };

    const formatDate = (isoString) => {
        if (!isoString) return '';
        try {
            return new Date(isoString).toLocaleDateString(isRtl ? 'fa-IR' : 'en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return isoString;
        }
    };

    const handleSendReply = async (e) => {
        e.preventDefault();
        if (!replyText.trim()) {
            setReplyError(t('متن پاسخ الزامی است', 'Reply text is required'));
            return;
        }
        setReplyError('');
        setSubmitting(true);
        try {
            const newReply = await messageService.replyToMessage(id, {
                text: replyText,
                attachment: replyAttachment
            });
            setMessage((prev) => ({
                ...prev,
                replies: [...(prev.replies || []), newReply]
            }));
            setReplyText('');
            setReplyAttachment(null);
        } catch (err) {
            console.error('Reply error:', err);
            setReplyError(t('خطا در ارسال پاسخ', 'Failed to send reply'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="max-w-[1136px] mx-auto px-3 sm:px-4 py-4 lg:py-8 font-iransansRegular overflow-hidden" dir={isRtl ? 'rtl' : 'ltr'}>
            <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
                <MessagesSidebar
                    activeKey="messages"
                    onNavigate={(key) => {
                        if (key === 'messages') navigate('/messages');
                        if (key === 'finance') navigate('/finance');
                        if (key === 'account') navigate('/account');
                        if (key === 'support') navigate('/support');
                        if (key === 'logout') navigate('/logout');
                    }}
                />

                <div className="flex-1 w-full min-w-0">
                    {/* Header */}
                    <div className="flex items-center gap-4 mb-6 lg:mb-8">
                        <motion.button
                            onClick={() => navigate('/messages')}
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                            className="px-4 py-2 rounded-lg text-white font-bold text-xs sm:text-sm shadow-md bg-[#204286] hover:bg-[#16305f] transition-colors whitespace-nowrap order-2"
                        >
                            {t('بازگشت به پیام‌ها', 'Back to Messages')}
                        </motion.button>
                        <motion.h1
                            className="text-xl sm:text-2xl lg:text-[32px] font-extrabold text-[#292DD8] order-1"
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            {t('مشاهده پیام', 'View Message')}
                        </motion.h1>
                    </div>

                    {/* Loading */}
                    {loading && (
                        <div className="flex justify-center items-center py-16">
                            <div className="w-10 h-10 border-4 border-[#292DD8] border-t-transparent rounded-full animate-spin"></div>
                        </div>
                    )}

                    {/* Error */}
                    {!loading && error && (
                        <div className="flex flex-col items-center gap-3 bg-red-50 rounded-xl p-6">
                            <p className="text-red-600 font-bold text-sm">{error}</p>
                            <button
                                onClick={() => navigate('/messages')}
                                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm"
                            >
                                {t('بازگشت', 'Go Back')}
                            </button>
                        </div>
                    )}

                    {!loading && !error && message && (
                        <>
                            {/* Original message */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                                className="mb-5"
                            >
                                <h2 className="text-base sm:text-lg font-bold text-[#204286] mb-3">
                                    {message.title}
                                </h2>

                                <div className="bg-[#EBF1FB] rounded-2xl p-4 sm:p-5">
                                    <p className="text-sm sm:text-base text-[#204286] leading-7 sm:leading-8 whitespace-pre-wrap">
                                        {message.text}
                                    </p>

                                    <div className="flex items-center justify-between flex-wrap gap-2 mt-3">
                                        {message.attachment && (
                                            <a
                                                href={message.attachment}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#204286] text-white text-xs sm:text-sm font-bold hover:bg-[#16305f] transition-colors"
                                            >
                                                {t('دانلود فایل ضمیمه', 'Download Attachment')}
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                                                </svg>
                                            </a>
                                        )}
                                        <span className="text-xs text-gray-400 mr-auto">{formatDate(message.created_at)}</span>
                                    </div>
                                </div>
                            </motion.div>

                            {/* Replies thread - chat bubble, alternating left/right by replier */}
                            {(message.replies || []).length > 0 && (
                                <div className="flex flex-col gap-3 mb-6">
                                    {message.replies.map((reply, idx) => {
                                        const isAdmin = reply.replier === 1; // 1 = ادمین
                                        return (
                                            <motion.div
                                                key={idx}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.3, delay: idx * 0.05 }}
                                                className={`flex ${isAdmin ? 'justify-start' : 'justify-end'}`}
                                            >
                                                <div
                                                    className={`max-w-[85%] sm:max-w-[70%] rounded-2xl p-3.5 sm:p-4 ${
                                                        isAdmin
                                                            ? 'bg-[#EBEBEB] text-[#204286]'
                                                            : 'bg-gradient-to-r from-[#292DD8] to-[#4B4FEE] text-white'
                                                    }`}
                                                >
                                                    <p className="text-xs sm:text-sm font-bold mb-1 opacity-80">
                                                        {isAdmin ? t('ادمین', 'Admin') : t('شما', 'You')}
                                                    </p>
                                                    <p className="text-sm sm:text-base leading-6 sm:leading-7 whitespace-pre-wrap">
                                                        {reply.text}
                                                    </p>
                                                    {reply.attachment && (
                                                        <a
                                                            href={reply.attachment}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className={`inline-flex items-center gap-1.5 mt-2 text-xs sm:text-sm font-bold underline ${
                                                                isAdmin ? 'text-[#204286]' : 'text-white'
                                                            }`}
                                                        >
                                                            {t('فایل ضمیمه', 'Attachment')}
                                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                                                            </svg>
                                                        </a>
                                                    )}
                                                    <p className={`text-[10px] sm:text-xs mt-2 ${isAdmin ? 'text-gray-500' : 'text-white/70'}`}>
                                                        {formatDate(reply.created_at)}
                                                    </p>
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Reply form */}
                            <motion.form
                                onSubmit={handleSendReply}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.1 }}
                            >
                                <h2 className="text-base sm:text-lg font-bold text-[#204286] mb-3">
                                    {t('پاسخ دادن', 'Reply')}
                                </h2>

                                <textarea
                                    value={replyText}
                                    onChange={(e) => setReplyText(e.target.value)}
                                    placeholder={t('متن پاسخ خود را بنویسید', 'Type your reply')}
                                    rows={5}
                                    className={`w-full bg-[#EBF1FB] border ${
                                        replyError ? 'border-red-400' : 'border-[#D6E1F5]'
                                    } rounded-xl px-4 py-3 text-sm sm:text-base text-[#204286] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#292DD8]/40 transition-all resize-none mb-1.5`}
                                />
                                {replyError && <p className="text-red-500 text-xs mb-3">{replyError}</p>}

                                <div className="mt-4 mb-5">
                                    <label className="block text-sm sm:text-base font-bold text-[#204286] mb-2">
                                        {t('آپلود عکس', 'Upload Image')}
                                        <span className="text-gray-400 font-normal text-xs sm:text-sm mr-1.5">
                                            {t('(اختیاری)', '(optional)')}
                                        </span>
                                    </label>
                                    <label
                                        htmlFor="reply-file-upload"
                                        className="flex items-center justify-between w-full bg-[#EBF1FB] border border-[#D6E1F5] rounded-xl px-4 py-3 text-sm sm:text-base text-gray-400 cursor-pointer hover:bg-[#E2EBFA] transition-all"
                                    >
                                        <span className={replyAttachment ? 'text-[#204286] font-medium truncate' : ''}>
                                            {replyAttachment ? replyAttachment.name : t('برای اضافه کردن عکس کلیک نمایید', 'Click to add an image')}
                                        </span>
                                        <svg className="w-5 h-5 text-[#292DD8] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                    </label>
                                    <input
                                        id="reply-file-upload"
                                        type="file"
                                        accept="image/*"
                                        onChange={handleReplyFileChange}
                                        className="hidden"
                                    />
                                </div>

                                <motion.button
                                    type="submit"
                                    disabled={submitting}
                                    whileHover={{ scale: submitting ? 1 : 1.02 }}
                                    whileTap={{ scale: submitting ? 1 : 0.98 }}
                                    className="px-8 py-2.5 rounded-lg text-white font-bold text-sm sm:text-base shadow-lg bg-gradient-to-r from-[#292DD8] to-[#4B4FEE] hover:from-[#1f23a8] hover:to-[#3a3ecc] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    {submitting ? t('در حال ارسال...', 'Sending...') : t('ارسال پاسخ', 'Send Reply')}
                                </motion.button>
                            </motion.form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MessageDetail;